<template>
  <quest-main title="Snippets"
    copy="Management of global property promotional materials / snippets / guarantees as well as exit intent banners"
    :loading="snippetContext.loading || exitIntentContext.loading">
    <template v-slot:head>
      <div>
        <router-link :to="{ name: 'snippets-add' }" class="d-sm-inline-block btn btn-sm btn-primary shadow-sm mr-2">

          <font-awesome-icon icon="plus" size="sm" class="text-white-50"></font-awesome-icon>
          Add Snippet</router-link>
        <router-link :to="{ name: 'exit-intents-add' }" class="d-sm-inline-block btn btn-sm btn-primary shadow-sm">
          <font-awesome-icon icon="plus" size="sm" class="text-white-50"></font-awesome-icon>
          Add Exit Intent</router-link>
      </div>
    </template>
    <template v-slot:body>

      <!-- Snippets collection -->
      <quest-card title="Snippets collection">
        <quest-table id="snippets-table" :columns="snippetContext.columns">
          <template v-if="snippetContext.results.items.length > 0">
            <tr class="small align-middle" v-for="snippet in snippetContext.results.items" :key="snippet.id">
              <td>
                <router-link :to="{ name: 'snippets-edit', params: { id: snippet.id } }">{{ snippet.name
                  }}</router-link>
              </td>
              <td>{{ lastModified(snippet.modifiedUTC) }}</td>
              <td>
                <b-dropdown no-caret variant="link" class="b-dropdown-p-0">
                  <template #button-content>
                    <font-awesome-icon icon="ellipsis-v" size="sm" fixed-width
                      class="text-gray-400"></font-awesome-icon>
                  </template>
                  <b-dropdown-header>Actions</b-dropdown-header>
                  <b-dropdown-item :to="{
                    name: 'snippets-edit',
                    params: { id: snippet.id },
                  }">View</b-dropdown-item>
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item-button @click="promptDeleteSnippet(snippet)" text="danger"
                    variant="danger">Delete</b-dropdown-item-button>
                </b-dropdown>
              </td>
            </tr>
          </template>
          <template v-if="snippetContext.results.items.length === 0">
            <tr class="align-middle">
              <td :colspan="snippetContext.results.length">
                <div class="small text-center py-5">
                  There are no snippets in the system
                </div>
              </td>
            </tr>
          </template>
        </quest-table>
        <quest-pager v-if="snippetContext.results.total > snippetContext.pageSize" @changePage="changePageSnippet"
          :currentPage="snippetContext.page" :itemCount="snippetContext.results.total"
          :pageSize="snippetContext.pageSize"></quest-pager>
      </quest-card>
      <b-modal v-model="snippetContext.showDeleteModal" centered title="Delete">
        <p class="my-4" v-if="snippetContext.toDelete">
          Are you sure you wish to delete snippet '{{ snippetContext.toDelete.name }}'?
        </p>
        <template v-slot:modal-footer>
          <button class="btn btn-secondary btn-sm" type="button" @click="cancelPromptDeleteSnippet">
            Cancel</button><button class="btn btn-danger btn-sm" type="button" @click="confirmPromptDeleteSnippet">
            Delete
          </button>
        </template>
      </b-modal>

      <!-- Exit Intent Banners -->
      <quest-card title="Exit intent banners">
        <quest-table id="exit-intents-table" :columns="exitIntentContext.columns">
          <template v-if="exitIntentContext.results.items.length > 0">
            <tr class="small align-middle" v-for="exitIntent in exitIntentContext.results.items" :key="exitIntent.id">
              <td>
                <router-link :to="{ name: 'exit-intents-edit', params: { id: exitIntent.id } }">{{ exitIntent.name
                  }}</router-link>
              </td>
              <td>{{ lastModified(exitIntent.modifiedUTC) }}</td>
              <td :class="exitIntent.isActive ? 'text-success' : 'text-danger'">{{ exitIntent.isActive ? 'Enabled' : 'Disabled' }}</td>
              <td>
                <b-dropdown no-caret variant="link" class="b-dropdown-p-0">
                  <template #button-content>
                    <font-awesome-icon icon="ellipsis-v" size="sm" fixed-width
                      class="text-gray-400"></font-awesome-icon>
                  </template>
                  <b-dropdown-header>Actions</b-dropdown-header>
                  <b-dropdown-item :to="{
                    name: 'exit-intents-edit',
                    params: { id: exitIntent.id },
                  }">View</b-dropdown-item>
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item-button @click="promptDeleteExitIntent(exitIntent)" text="danger"
                    variant="danger">Delete</b-dropdown-item-button>
                </b-dropdown>
              </td>
            </tr>
          </template>
          <template v-if="exitIntentContext.results.items.length === 0">
            <tr class="align-middle">
              <td :colspan="exitIntentContext.columns.length">
                <div class="small text-center py-5">
                  There are no exit intents in the system
                </div>
              </td>
            </tr>
          </template>
        </quest-table>
        <quest-pager v-if="exitIntentContext.results.total > exitIntentContext.pageSize"
          @changePage="changePageExitIntent" :currentPage="exitIntentContext.page"
          :itemCount="exitIntentContext.results.total" :pageSize="exitIntentContext.pageSize"></quest-pager>
      </quest-card>
      <b-modal v-model="exitIntentContext.showDeleteModal" centered title="Delete">
        <p class="my-4" v-if="exitIntentContext.toDelete">
          Are you sure you wish to delete exit intent banner '{{ exitIntentContext.toDelete.name }}'?
        </p>
        <template v-slot:modal-footer>
          <button class="btn btn-secondary btn-sm" type="button" @click="cancelPromptDeleteExitIntent">
            Cancel</button><button class="btn btn-danger btn-sm" type="button" @click="confirmPromptDeleteExitIntent">
            Delete
          </button>
        </template>
      </b-modal>

    </template>
  </quest-main>
</template>

<script>
import { success, error } from '../helpers'
import moment from 'moment'
import 'moment-timezone'
import {
  QuestMain,
  QuestTable,
  QuestCard,
  QuestModal,
  QuestPager,
} from './structure'
import { SnippetService, ExitIntentService } from '../services'
import PagedResult from '../models/paged-result'

export default {
  name: 'snippets',
  components: {
    QuestMain,
    QuestTable,
    QuestCard,
    QuestPager,
    QuestModal,
  },
  data() {
    return {
      snippetContext: {
        pageSize: 10,
        page: 1,
        term: '',
        results: new PagedResult(),
        columns: ['Name', 'Modified', ''],
        loading: true,
        toDelete: null,
        showDeleteModal: false,
      },
      exitIntentContext: {
        pageSize: 10,
        page: 1,
        term: '',
        results: new PagedResult(),
        columns: ['Name', 'Modified', 'Status', ''],
        loading: true,
        toDelete: null,
        showDeleteModal: false,
      }
    }
  },
  mounted() {
    this.changePageSnippet(1)
    this.changePageExitIntent(1)
  },
  methods: {
    changePageSnippet(page) {
      new SnippetService()
        .list(page, this.snippetContext.pageSize, this.snippetContext.term)
        .then((data) => {
          this.snippetContext.results = data
          this.snippetContext.page = page
        })
        .finally((_) => (this.snippetContext.loading = false))
    },
    changePageExitIntent(page) {
      new ExitIntentService()
        .list(page, this.exitIntentContext.pageSize, this.exitIntentContext.term)
        .then((data) => {
          this.exitIntentContext.results = data
          this.exitIntentContext.page = page
        })
        .finally((_) => (this.exitIntentContext.loading = false))
    },
    lastModified(date) {
      return moment(`${date}Z`).tz('Australia/Melbourne').format('lll')
    },
    promptDeleteSnippet(item) {
      this.snippetContext.toDelete = item
      this.snippetContext.showDeleteModal = true
    },
    promptDeleteExitIntent(item) {
      this.exitIntentContext.toDelete = item
      this.exitIntentContext.showDeleteModal = true
    },
    cancelPromptDeleteSnippet() {
      this.snippetContext.showDeleteModal = false
      this.snippetContext.toDelete = null
    },
    cancelPromptDeleteExitIntent() {
      this.exitIntentContext.showDeleteModal = false
      this.exitIntentContext.toDelete = null
    },
    confirmPromptDeleteSnippet() {
      new SnippetService()
        .delete(this.snippetContext.toDelete.id)
        .then((data) => {
          this.snippetContext.showDeleteModal = false

          if (data) {
            this.changePageSnippet(1)
            success(`Deleted snippet #`, this.snippetContext.toDelete.name)
          } else {
            error(`Failed to delete snippet #`, this.snippetContext.toDelete.name)
          }
        })
        .finally((_) => {
          this.snippetContext.toDelete = null
        })
    },
    confirmPromptDeleteExitIntent() {
      new ExitIntentService()
        .delete(this.exitIntentContext.toDelete.id)
        .then((data) => {
          this.exitIntentContext.showDeleteModal = false

          if (data) {
            this.changePageExitIntent(1)
            success(`Deleted exit intent banner #`, this.exitIntentContext.toDelete.name)
          } else {
            error(`Failed to delete exit intent banner #`, this.exitIntentContext.toDelete.name)
          }
        })
        .finally((_) => {
          this.exitIntentContext.toDelete = null
        })
    },
  },
}
</script>
